import React from 'react';
import { Link } from 'react-router-dom';
import { Trophy, Calendar, Users, Globe, ArrowRight, Star } from 'lucide-react';

const HomePage = () => {
  const features = [
    {
      icon: <Trophy className="w-12 h-12 text-amber-600" />,
      title: "WK 2026",
      description: "Alles over het Wereldkampioenschap in Noord-Amerika. Wedstrijden, stadions, en het Nederlands elftal.",
      link: "/wk-2026"
    },
    {
      icon: <Calendar className="w-12 h-12 text-amber-600" />,
      title: "Wedstrijden",
      description: "Complete speelschema's, uitslagen, en standen van alle poules en knock-outfases.",
      link: "/wedstrijden"
    },
    {
      icon: <Users className="w-12 h-12 text-amber-600" />,
      title: "Oranje",
      description: "Volg het Nederlands elftal op weg naar de wereldtitel. Selectie, statistieken, en nieuws.",
      link: "/oranje"
    }
  ];

  const recentNews = [
    {
      title: "Nederland lot in poule A bij WK 2026",
      excerpt: "Oranje begint aan WK-campagne tegen Mexico op 14 juni in Los Angeles.",
      category: 'Nieuws',
      date: '29 maart 2026'
    },
    {
      title: "Deze stadions hosten WK-wedstrijden",
      excerpt: "Van New York tot Los Angeles: dit zijn de 16 stadions waar het WK wordt gespeeld.",
      category: 'Stadions',
      date: '28 maart 2026'
    },
    {
      title: "WK 2026: Dit zijn de favorieten",
      excerpt: "Brazilië, Argentinië, Frankrijk en Nederland horen bij de topfavorieten.",
      category: 'Analyse',
      date: '27 maart 2026'
    }
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-amber-600 to-orange-600 text-white py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="flex justify-center mb-6">
              <Trophy className="w-20 h-20" />
            </div>
            <h1 className="text-6xl font-bold mb-6">WK Informatie</h1>
            <p className="text-2xl mb-8 opacity-90">
              Jouw complete bron voor het Wereldkampioenschap Voetbal 2026
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/wk-2026" className="bg-white text-amber-700 px-8 py-4 rounded-full font-semibold text-lg hover:bg-amber-50 transition flex items-center">
                <Trophy className="mr-2" /> WK 2026
              </Link>
              <Link to="/wedstrijden" className="border-2 border-white text-white px-8 py-4 rounded-full font-semibold text-lg hover:bg-white hover:text-amber-700 transition">
                <Calendar className="mr-2" /> Wedstrijden
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Countdown */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold mb-8">Aftellen naar WK 2026</h2>
            <div className="grid grid-cols-4 gap-4">
              <div className="bg-amber-50 p-6 rounded-xl">
                <div className="text-4xl font-bold text-amber-600">72</div>
                <div className="text-gray-600">Dagen</div>
              </div>
              <div className="bg-amber-50 p-6 rounded-xl">
                <div className="text-4xl font-bold text-amber-600">14</div>
                <div className="text-gray-600">Uur</div>
              </div>
              <div className="bg-amber-50 p-6 rounded-xl">
                <div className="text-4xl font-bold text-amber-600">32</div>
                <div className="text-gray-600">Minuten</div>
              </div>
              <div className="bg-amber-50 p-6 rounded-xl">
                <div className="text-4xl font-bold text-amber-600">18</div>
                <div className="text-gray-600">Seconden</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold mb-12 text-center text-gray-800">
            Alles over het WK
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {features.map((feature, index) => (
              <Link to={feature.link} key={index} className="bg-white rounded-xl shadow-md p-8 hover:shadow-xl transition transform hover:-translate-y-2">
                <div className="mb-4">{feature.icon}</div>
                <h3 className="text-2xl font-bold mb-3 text-gray-800">{feature.title}</h3>
                <p className="text-gray-600 mb-4">{feature.description}</p>
                <span className="text-amber-600 font-semibold flex items-center">
                  Meer informatie <ArrowRight className="ml-2" size={18} />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Quick Facts */}
      <section className="py-16 bg-gradient-to-r from-amber-50 to-orange-50">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold mb-12 text-center text-gray-800">
            WK 2026 Feiten
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-6xl mx-auto">
            <div className="text-center">
              <div className="text-4xl font-bold text-amber-600 mb-2">48</div>
              <div className="text-gray-600">Deelnemende Landen</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-amber-600 mb-2">104</div>
              <div className="text-gray-600">Wedstrijden</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-amber-600 mb-2">16</div>
              <div className="text-gray-600">Gaststeden</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-amber-600 mb-2">3</div>
              <div className="text-gray-600">Gastlanden</div>
            </div>
          </div>
        </div>
      </section>

      {/* Recent News */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold mb-12 text-center text-gray-800">
            Laatste Nieuws
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {recentNews.map((news, index) => (
              <article key={index} className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-xl transition">
                <img src={`https://picsum.photos/seed/wk${index}/400/200`} alt="Nieuws" className="w-full h-48 object-cover" />
                <div className="p-6">
                  <div className="flex items-center text-sm text-gray-500 mb-3">
                    <span className="bg-amber-100 text-amber-800 px-3 py-1 rounded-full font-semibold mr-3">
                      {news.category}
                    </span>
                    {news.date}
                  </div>
                  <h3 className="text-xl font-bold mb-3 text-gray-800">{news.title}</h3>
                  <p className="text-gray-600 mb-4">{news.excerpt}</p>
                  <Link to={`/nieuws/${index}`} className="text-amber-600 font-semibold hover:text-amber-800">
                    Lees meer →
                  </Link>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Gastlanden */}
      <section className="py-16 bg-gradient-to-r from-amber-50 to-orange-50">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold mb-12 text-center text-gray-800">
            Gastlanden
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            <div className="bg-white rounded-xl shadow-md p-8 text-center">
              <div className="text-6xl mb-4">🇺🇸</div>
              <h3 className="text-2xl font-bold mb-3">Verenigde Staten</h3>
              <p className="text-gray-600">11 steden hosten WK-wedstrijden</p>
            </div>
            <div className="bg-white rounded-xl shadow-md p-8 text-center">
              <div className="text-6xl mb-4">🇲🇽</div>
              <h3 className="text-2xl font-bold mb-3">Mexico</h3>
              <p className="text-gray-600">3 steden hosten WK-wedstrijden</p>
            </div>
            <div className="bg-white rounded-xl shadow-md p-8 text-center">
              <div className="text-6xl mb-4">🇨🇦</div>
              <h3 className="text-2xl font-bold mb-3">Canada</h3>
              <p className="text-gray-600">2 steden hosten WK-wedstrijden</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gradient-to-r from-amber-600 to-orange-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6">Bereid je voor op het WK!</h2>
          <p className="text-xl mb-8 opacity-90 max-w-2xl mx-auto">
            Ontvang het laatste nieuws, updates en exclusieve content over het WK 2026 direct in je mailbox.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/nieuwsbrief" className="bg-white text-amber-700 px-8 py-4 rounded-full font-semibold text-lg hover:bg-amber-50 transition">
              Schrijf je in voor Nieuwsbrief
            </Link>
            <Link to="/over-wk" className="border-2 border-white text-white px-8 py-4 rounded-full font-semibold text-lg hover:bg-white hover:text-amber-700 transition">
              Meer over WK Geschiedenis
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;